import { useAtom } from "jotai";
import dashboardAtoms, {
  fetchBlocks,
  fetchBomTrackerTypes,
  fetchBulkData,
  fetchMapSize,
  fetchPartsAtom,
  fetchPartTrackerTypes,
  fetchpierlegends,
  fetchProjectBom,
  fetchTrackers,
  fetchTypeData,
  getActivity,
  getConstructionProgressList,
} from "../../../atoms/Dashboard/DashboardAtom";
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProgressComponent from "../ProgressComponent";
import PiersComponent from "./PierComponent";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import { getSelectedRows, SectionList, SectionListCount } from "../../../shared/Helper";
import { useParams } from "react-router-dom/dist";
import { handleSelectionChange } from "@product-fusion/surya-fw/dist/utils/selectionUtils";

const Mapview = () => {
  const [data] = useAtom(dashboardAtoms);
  const [, setActivity] = useAtom(getActivity);
  const [, setProgress] = useAtom(getConstructionProgressList);
  const [, setBulk] = useAtom(fetchBulkData);
  const [, setTrackers] = useAtom(fetchTrackers);
  const [, setPiers] = useAtom(fetchpierlegends);
  const [, setBlocks] = useAtom(fetchTypeData);
  const [, setMapSize] = useAtom(fetchMapSize);
  const [, setPartTrackerTypes] = useAtom(fetchPartTrackerTypes);
  const [, setPart] = useAtom(fetchPartsAtom);
  const [, setBomTracker] = useAtom(fetchBomTrackerTypes);
  const [, setProjectBom] = useAtom(fetchProjectBom);
  const [loader, setLoader] = useState(false);
  const [visible, setVisible] = useState(true);
  const [downloadD3Loading, setDownloadD3Loading] = useState(false)
  const [currentSelection, setCurrentSelection] = useState("");
  const containerRef = useRef(null);
  const [, setBlock] = useAtom(fetchBlocks);

  const projectId = localStorage.getItem("project_id");
  const path = useLocation();
  const [settingsData] = useAtom(settingsAtom);
  const [selectedRow, setSelectedRow] = useState({
    count: null,
    sections: {},
    trackerTypes: {}
  });
  const [activity, setActivities] = useState("None select");
  const [section, setSection] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const params = useParams();
  const [trackers, setTracker] = useState([]);
  const [openSelectedTrackersModal, setOpenSelectedTrackersModal] = useState(false)
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")))
  const [activityType, setActivityType] = useState("")

  const handleSectionChange = async (event) => {
    const value = event.target.value;
    const data = section.filter((item) => !value.includes(item))

    setSection(typeof value === 'string' ? value.split(',') : value);

    const sectionValue = await handleSelectionChange("section", value, data);
    setTracker(Object.keys(sectionValue?.trackerTypes)?.map(item => Number(item.split('_')[1])))
    setSelectedRow(sectionValue);
  };

  const handleSelectAll = async () => {
    const sectionValue = await handleSelectionChange("selectAll");
    setTracker(Object.keys(sectionValue?.trackerTypes)?.map(item => Number(item.split('_')[1])))
    setSelectedRow(sectionValue);
    setSelectAll(true)
    setSection(SectionList(data?.bulkAllData?.piers))
  }

  const fetchAllData = useCallback(async () => {
    setLoader(true);
    try {
      const projectKey = path?.pathname?.split("/")?.[2];
      await Promise.allSettled([
        setActivity(""),
        setProgress(projectKey),
        setBulk(projectKey),
        setPiers(""),
        setBlocks(projectKey),
        setMapSize(projectKey),
        setPart(''),
        setPartTrackerTypes(),
        setBomTracker(),
        setProjectBom(),
        setBlock()
      ]);
      
    } catch (error) {
      console.error("❌ Error in fetchAllData:", error);
    } finally {
      setLoader(false);
    }
  }, [path?.pathname, setActivity, setProgress, setBulk, setPiers, setBlocks, setMapSize, setBlock, setPart, setBomTracker, setProjectBom, setPartTrackerTypes]);

  useEffect(() => {
    if (params?.id) {
      setTrackers(params?.id)
    }
  }, [params, setTrackers])

  useEffect(() => {
    if (projectId && path?.pathname?.split("/")?.[2]) {
      fetchAllData();
    }
  }, [projectId, fetchAllData, path?.pathname]);

  const handleClear = async () => {
    await handleSelectionChange("clear")
    setSelectedRow({
      count: null,
      sections: {},
      trackerTypes: {}
    })
    setSelectAll(false)
    setTracker([])
    setSection([])
  }

  const handleClickTracker = async (type, value) => {
    if (!params?.id) return;

    let trackerIds = [];

    if (type === "parent") {
      trackerIds = value.children?.map(child => child.tracker_type_id) || [];
    } else {
      trackerIds = [value?.tracker_type_id];
    }

    const shouldRemove = type === "parent"
      ? trackerIds.every(id => trackers.includes(id))
      : trackers.includes(trackerIds[0]);

    let updatedTrackers;
    if (shouldRemove) {
      updatedTrackers = trackers.filter(id => !trackerIds.includes(id));
    } else {
      updatedTrackers = [...new Set([...trackers, ...trackerIds])];
    }

    setTracker(updatedTrackers);

    const sectionValue = await handleSelectionChange(
      "trackerType",
      updatedTrackers,
      trackers.filter(id => trackerIds.includes(id))
    );
    setSelectedRow(sectionValue);

    const sectionWiseValue = SectionListCount(data?.bulkAllData?.piers);

    const matchingSections = [];
    if (sectionWiseValue && sectionValue?.sections) {
      Object.keys(sectionWiseValue).forEach(key => {
        const sectionNumber = key.replace('sections_', '');
        const formattedKey = `section_${sectionNumber}`; // ✅ match the second object key

        if (
          sectionValue.sections[formattedKey] &&
          sectionWiseValue[key]?.selectedCount === sectionValue.sections[formattedKey]?.selectedCount
        ) {
          matchingSections.push(parseInt(sectionNumber));
        }
      });
    }

    setSection(matchingSections)
  }


  return (
    <Box sx={{ display: 'flex', width: '100%', position: "relative" }}>
      <Box sx={{ height: 'calc(100vh - 116px)', width: visible ? "65%" : "100%", borderRight: '1px solid #e0e0e0' }}>
        <PiersComponent
          loader={loader}
          title={currentSelection}
          data={data}
          containerRef={containerRef}
          projectList={settingsData?.projectList}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          activity={activity}
          activityType={activityType}
          section={section}
          setSection={setSection}
          handleSectionChange={handleSectionChange}
          selectAll={selectAll}
          handleSelectAll={handleSelectAll}
          handleClear={handleClear}
          openSelectedTrackersModal={openSelectedTrackersModal}
          setOpenSelectedTrackersModal={setOpenSelectedTrackersModal}
          setDownloadD3Loading={setDownloadD3Loading}
          downloadD3Loading={downloadD3Loading}
          currentProject={currentProject}
        />
      </Box>
      {visible ?
        <Box sx={{ height: 'calc(100vh - 116px)', width: '35%' }}>
          <ProgressComponent
            loader={loader}
            data={data}
            setCurrentSelection={setCurrentSelection}
            setActivities={setActivities}
            setActivityType={setActivityType}
            handleClickTracker={handleClickTracker}
            trackers={trackers}
            selectedRow={selectedRow}
            isSvg={true}
            selectPart={getSelectedRows(selectedRow?.sections)}
            activity={activity}
            setSelectedRow={setSelectedRow}
            setSection={setSection}
            setTracker={setTracker}
            currentProject={currentProject}
          />
        </Box> : ''}
      <Box
        onClick={() => setVisible(!visible)}
        style={{ right: visible ? '33%' : 0 }}
        sx={{
          height: "24px",
          border: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "white",
          zIndex: 10,
          position: "absolute",
          top: 18,
          width: "24px",
          cursor: "pointer"
        }}
      >
        {visible ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </Box>
    </Box>
  );
};

export default Mapview;

